import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountsApi, storesApi } from '../services/api';
import type { AccountWithStores, AccountUpdateRequest } from 'shared';
import { formatCurrency } from 'shared';

interface Props {
  account: AccountWithStores;
  onClose: () => void;
}

export function AccountEditDrawer({ account, onClose }: Props) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: account.name,
    contact_name: account.contact_name || '',
    contact_phone: account.contact_phone || '',
    notes: account.notes || '',
  });

  const linkedIds = new Set(account.stores.map((s) => s.id));
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set(linkedIds));

  const { data: allStores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => storesApi.list(),
  });

  // Show stores already in this account + standalone stores (not in any account)
  const assignableStores = allStores
    .filter((s) => s.account_id === null || s.account_id === account.id)
    .sort((a, b) => a.name.localeCompare(b.name));

  const toggleStore = (storeId: string) => {
    setSelectedStoreIds((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const updateMutation = useMutation({
    mutationFn: async (data: AccountUpdateRequest) => {
      await accountsApi.update(account.id, data);

      const toAdd = [...selectedStoreIds].filter((id) => !linkedIds.has(id));
      const toRemove = [...linkedIds].filter((id) => !selectedStoreIds.has(id));

      await Promise.all([
        ...toAdd.map((id) => storesApi.update(id, { account_id: account.id })),
        ...toRemove.map((id) => storesApi.update(id, { account_id: null })),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => accountsApi.delete(account.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      name: formData.name,
      contact_name: formData.contact_name || undefined,
      contact_phone: formData.contact_phone || undefined,
      notes: formData.notes || undefined,
    });
  };

  return (
    <div className="fixed inset-0 overflow-hidden z-[1100]">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full sm:max-w-md bg-white shadow-xl">
        <div className="h-full flex flex-col">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Edit Account</h3>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Account fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Account Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, contact_name: e.target.value }))}
                  placeholder="Buyer / Owner / Main contact"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, contact_phone: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              {/* Store assignment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Linked Stores
                  <span className="ml-1.5 text-xs font-normal text-gray-400">
                    ({selectedStoreIds.size} selected)
                  </span>
                </label>

                {assignableStores.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No standalone stores available to add.</p>
                ) : (
                  <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-52 overflow-y-auto">
                    {assignableStores.map((store) => (
                      <label
                        key={store.id}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStoreIds.has(store.id)}
                          onChange={() => toggleStore(store.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{store.name}</p>
                          {(store.address_line1 || store.city) && (
                            <p className="text-xs text-gray-500 truncate">
                              {store.address_line1}{store.city && `, ${store.city}`}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Priority summary */}
              {account.priority && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Account Summary</h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-gray-500">Account Size</dt>
                    <dd className="text-gray-900 capitalize">{account.priority.account_size || '-'}</dd>
                    <dt className="text-gray-500">YTD Revenue</dt>
                    <dd className="text-gray-900">{formatCurrency(account.priority.ytd_revenue_cents)}</dd>
                  </dl>
                </div>
              )}

              {updateMutation.isError && (
                <p className="text-red-600 text-sm">Failed to save. Please try again.</p>
              )}

              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Delete "${account.name}"? Child stores will become standalone.`)) {
                      deleteMutation.mutate();
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete Account'}
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
